import { useState, useEffect, useCallback } from 'react';
import {
  DEFAULT_CAPTION_COLOR,
  DEFAULT_TEMPLATE_ID,
  DEFAULT_VISUAL_EFFECTS,
  DEFAULT_BACKGROUND_TYPE,
  DEFAULT_BACKGROUND_PALETTE,
  DEFAULT_TEXT_ANIMATION,
  DEFAULT_CAPTION_FONT,
  getInitialVisualEffects,
} from '../constants/defaults';

/**
 * useProductApprovalState Hook
 *
 * Manages all state for product proposal approval:
 * - Caption text and color
 * - Template selection and variables
 * - Visual effects (sparkles, bokeh, etc.)
 * - Background type and palette
 * - Text animation
 * - Caption font
 * - AI suggestion tracking
 *
 * @param {Object} item - The product proposal item
 * @returns {Object} State values, setters, and computed values
 */
export function useProductApprovalState(item) {
  // ===========================================================================
  // Caption State
  // ===========================================================================
  const [productCaption, setProductCaption] = useState(item?.caption_text || '');
  const [captionColor, setCaptionColor] = useState(item?.caption_color || DEFAULT_CAPTION_COLOR);
  const [captionFont, setCaptionFont] = useState(
    item?.template_variables?.caption_font ||
    item?.llm_suggestion?.style?.captionFont ||
    DEFAULT_CAPTION_FONT
  );

  // ===========================================================================
  // Template State
  // ===========================================================================
  const [templateVariables, setTemplateVariables] = useState(item?.template_variables || {});
  const [selectedTemplate, setSelectedTemplate] = useState(item?.template_id || DEFAULT_TEMPLATE_ID);

  // ===========================================================================
  // Visual Effects State
  // ===========================================================================
  const [visualEffects, setVisualEffects] = useState(getInitialVisualEffects(item));

  // ===========================================================================
  // Background State
  // ===========================================================================
  const [backgroundType, setBackgroundType] = useState(
    item?.template_variables?.background_type ||
    item?.llm_suggestion?.style?.backgroundType ||
    DEFAULT_BACKGROUND_TYPE
  );
  const [backgroundPalette, setBackgroundPalette] = useState(
    item?.template_variables?.background_palette ||
    item?.llm_suggestion?.style?.backgroundPalette ||
    DEFAULT_BACKGROUND_PALETTE
  );

  // ===========================================================================
  // Text Animation State
  // ===========================================================================
  const [textAnimation, setTextAnimation] = useState(
    item?.template_variables?.text_animation ||
    item?.llm_suggestion?.style?.textAnimation ||
    DEFAULT_TEXT_ANIMATION
  );

  // ===========================================================================
  // AI Suggestion State
  // ===========================================================================
  const [aiSuggestionApplied, setAiSuggestionApplied] = useState(false);

  // ===========================================================================
  // Reject State
  // ===========================================================================
  const [productRejectReason, setProductRejectReason] = useState('');

  // ===========================================================================
  // PAngV Price Disclaimer State (EU Price Indication Regulation)
  // ===========================================================================
  const [showPriceDisclaimer, setShowPriceDisclaimer] = useState(true); // Default: ON

  // ===========================================================================
  // Reset State on Item Change
  // ===========================================================================
  useEffect(() => {
    if (item) {
      setProductCaption(item.caption_text || '');
      setCaptionColor(item.caption_color || DEFAULT_CAPTION_COLOR);
      setCaptionFont(
        item.template_variables?.caption_font ||
        item.llm_suggestion?.style?.captionFont ||
        DEFAULT_CAPTION_FONT
      );
      setTemplateVariables(item.template_variables || {});
      setSelectedTemplate(item.template_id || DEFAULT_TEMPLATE_ID);
      setVisualEffects(getInitialVisualEffects(item));
      setBackgroundType(
        item.template_variables?.background_type ||
        item.llm_suggestion?.style?.backgroundType ||
        DEFAULT_BACKGROUND_TYPE
      );
      setBackgroundPalette(
        item.template_variables?.background_palette ||
        item.llm_suggestion?.style?.backgroundPalette ||
        DEFAULT_BACKGROUND_PALETTE
      );
      setTextAnimation(
        item.template_variables?.text_animation ||
        item.llm_suggestion?.style?.textAnimation ||
        DEFAULT_TEXT_ANIMATION
      );
      setAiSuggestionApplied(false);
      setProductRejectReason('');
      // Reset price disclaimer to default ON
      setShowPriceDisclaimer(true);
    }
  }, [item]);

  // ===========================================================================
  // Handlers
  // ===========================================================================

  /**
   * Handle template variables change
   */
  const handleTemplateVariablesChange = useCallback((newVariables) => {
    setTemplateVariables(newVariables);
  }, []);

  /**
   * Apply all AI suggestions at once
   */
  const handleApplyAISuggestion = useCallback((suggestion) => {
    if (!suggestion) return;

    // Apply effects if suggested
    if (suggestion.effects) {
      setVisualEffects(suggestion.effects);
    }

    // Apply style/colors if suggested
    if (suggestion.style) {
      if (suggestion.style.captionColor) {
        setCaptionColor(suggestion.style.captionColor);
      }
      if (suggestion.style.backgroundType) {
        setBackgroundType(suggestion.style.backgroundType);
      }
      if (suggestion.style.backgroundPalette) {
        setBackgroundPalette(suggestion.style.backgroundPalette);
      }
      if (suggestion.style.captionFont) {
        setCaptionFont(suggestion.style.captionFont);
      }
      if (suggestion.style.textAnimation) {
        setTextAnimation(suggestion.style.textAnimation);
      }
    }

    // Apply texts if suggested
    if (suggestion.texts?.caption) {
      setProductCaption(suggestion.texts.caption);
    }

    setAiSuggestionApplied(true);
  }, []);

  // ===========================================================================
  // Computed Values
  // ===========================================================================

  /**
   * Build AI suggestion object from item data
   */
  const aiSuggestion = item?.llm_suggestion ? {
    templateId: item.llm_suggestion.templateId || item.template_id,
    effects: item.llm_suggestion.effects,
    style: item.llm_suggestion.style,
    texts: item.llm_suggestion.texts,
    reasoning: item.llm_suggestion.reasoning,
  } : null;

  /**
   * Extract product info from template variables
   */
  const productInfo = {
    productTitle: templateVariables?.product_title ||
      item?.template_variables?.product_title ||
      item?.product_id ||
      'Product',
    salePrice: templateVariables?.sale_price || item?.template_variables?.sale_price,
    originalPrice: templateVariables?.original_price || item?.template_variables?.original_price,
    discountPercent: templateVariables?.discount_percent || item?.template_variables?.discount_percent,
  };

  /**
   * Check if video is currently rendering
   */
  const isRendering = item?.status === 'rendering_video';

  /**
   * Get preview URL
   */
  const previewUrl = item?.preview_url || item?.sas_url;

  // ===========================================================================
  // Return
  // ===========================================================================
  return {
    // Caption
    productCaption,
    setProductCaption,
    captionColor,
    setCaptionColor,
    captionFont,
    setCaptionFont,

    // Template
    templateVariables,
    setTemplateVariables,
    selectedTemplate,
    setSelectedTemplate,
    handleTemplateVariablesChange,

    // Visual Effects
    visualEffects,
    setVisualEffects,

    // Background
    backgroundType,
    setBackgroundType,
    backgroundPalette,
    setBackgroundPalette,

    // Text Animation
    textAnimation,
    setTextAnimation,

    // AI Suggestion
    aiSuggestion,
    aiSuggestionApplied,
    handleApplyAISuggestion,

    // Reject
    productRejectReason,
    setProductRejectReason,

    // PAngV Price Disclaimer
    showPriceDisclaimer,
    setShowPriceDisclaimer,

    // Computed
    productInfo,
    isRendering,
    previewUrl,
  };
}

export default useProductApprovalState;
